import React, { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import {
  getCurrentLocation,
  getAddressFromCoordinates,
  checkLocationPermission,
  formatLocationForMessage,
  getTileLayerUrl,
  getTileLayerAttribution
} from '../utils/locationUtils';
import '../styles/LocationPicker.css';

// Custom component to handle map clicks
function MapClickHandler({ onLocationSelect }) {
  const map = useMapEvents({
    click: (e) => {
      const { lat, lng } = e.latlng;
      onLocationSelect({
        latitude: lat,
        longitude: lng,
        accuracy: 0,
        timestamp: new Date().toISOString()
      });
    }
  });
  return null;
}

function LocationPicker({ isVisible, onLocationSelect, onClose }) {
  const [currentLocation, setCurrentLocation] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [address, setAddress] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasPermission, setHasPermission] = useState(false);

  // Check location permission on mount
  useEffect(() => {
    if (isVisible) {
      checkLocationPermission().then(setHasPermission);
    }
  }, [isVisible]);

  // Get current location when component becomes visible
  useEffect(() => {
    if (isVisible && !currentLocation) {
      getCurrentUserLocation();
    }
  }, [isVisible]);

  const getCurrentUserLocation = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const location = await getCurrentLocation();
      setCurrentLocation(location);
      setSelectedLocation(location);
      setHasPermission(true);

      // Get address for current location
      const locationAddress = await getAddressFromCoordinates(
        location.latitude,
        location.longitude
      );
      setAddress(locationAddress);
    } catch (err) {
      setError(err.message);
      console.error('Error getting location:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMapClick = useCallback(async (clickedLocation) => {
    setSelectedLocation(clickedLocation);
    setIsLoading(true);

    try {
      const locationAddress = await getAddressFromCoordinates(
        clickedLocation.latitude,
        clickedLocation.longitude
      );
      setAddress(locationAddress);
    } catch (err) {
      console.error('Error getting address:', err);
      setAddress(`${clickedLocation.latitude.toFixed(6)}, ${clickedLocation.longitude.toFixed(6)}`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleSendLocation = () => {
    if (selectedLocation && onLocationSelect) {
      const locationData = formatLocationForMessage(selectedLocation, address);
      onLocationSelect(locationData);
    }
    onClose();
  };

  const handleClose = () => {
    setCurrentLocation(null);
    setSelectedLocation(null);
    setAddress('');
    setError(null);
    onClose();
  };

  if (!isVisible) return null;

  return (
    <div className="location-picker-overlay">
      <div className="location-picker">
        <div className="location-picker-header">
          <h3>Share Location</h3>
          <button className="close-btn" onClick={handleClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="location-picker-content">
          {error && (
            <div className="location-error">
              <p>{error}</p>
              <button
                className="retry-btn"
                onClick={getCurrentUserLocation}
                disabled={isLoading}
              >
                Try Again
              </button>
            </div>
          )}

          {!error && (
            <>
              <div className="location-controls">
                <button
                  className="current-location-btn"
                  onClick={getCurrentUserLocation}
                  disabled={isLoading}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <polygon points="16.24,7.76 14.12,14.12 7.76,16.24 9.88,9.88 16.24,7.76" />
                  </svg>
                  Use Current Location
                </button>
              </div>

              <div className="map-container">
                {currentLocation ? (
                  <MapContainer
                    center={[currentLocation.latitude, currentLocation.longitude]}
                    zoom={15}
                    style={{ width: '100%', height: '300px' }}
                    scrollWheelZoom={true}
                  >
                    <TileLayer
                      url={getTileLayerUrl()}
                      attribution={getTileLayerAttribution()}
                    />
                    <MapClickHandler onLocationSelect={handleMapClick} />
                    {selectedLocation && (
                      <Marker
                        position={[selectedLocation.latitude, selectedLocation.longitude]}
                      />
                    )}
                  </MapContainer>
                ) : (
                  <div className="map-loading">
                    <div className="loading-spinner"></div>
                    <p>Getting your location...</p>
                  </div>
                )}
              </div>

              {address && (
                <div className="location-info">
                  <div className="address-display">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    <span>{address}</span>
                  </div>

                  {selectedLocation && (
                    <div className="coordinates">
                      <small>
                        {selectedLocation.latitude.toFixed(6)}, {selectedLocation.longitude.toFixed(6)}
                      </small>
                    </div>
                  )}
                </div>
              )}

              <div className="location-picker-footer">
                <button
                  className="cancel-btn"
                  onClick={handleClose}
                >
                  Cancel
                </button>
                <button
                  className="send-location-btn"
                  onClick={handleSendLocation}
                  disabled={!selectedLocation || isLoading}
                >
                  Send Location
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default LocationPicker;
